import type { ReactElement } from 'react';
import { estimateSharing, structuralStats } from '../engine';
import type { GameConfig, Grid } from '../engine';
import { formatDecimal, formatInt, formatPercent, gridToText } from '../lib/format';
import { Balls } from './Balls';

interface GridCardProps {
  game: GameConfig;
  grid: Grid;
  index?: number;
  /** Rendered on the right of the header. */
  badge?: ReactElement | null;
  actions?: ReactElement | null;
  hitMain?: readonly number[];
  hitBonus?: readonly number[];
}

/**
 * Vocabulary here is fixed by CLAUDE.md: "indice de popularité", "co-gagnants
 * attendus", "part de lot espérée". No confidence score, no prediction, no hot or
 * cold numbers — those would be lies dressed as features.
 */
export function GridCard({
  game,
  grid,
  index,
  badge = null,
  actions = null,
  hitMain,
  hitBonus,
}: GridCardProps): ReactElement {
  const stats = structuralStats(grid.main, game);
  const sharing = estimateSharing(grid, game);

  return (
    <div className="card fade-in">
      <div className="card-head">
        <span className="card-title">
          {index === undefined ? game.label : `Grille ${index}`}
        </span>
        {badge}
      </div>

      <Balls
        main={grid.main}
        bonus={grid.bonus}
        bonusLabel={game.bonusLabel}
        {...(hitMain === undefined ? {} : { hitMain })}
        {...(hitBonus === undefined ? {} : { hitBonus })}
      />

      <div className="metrics">
        <span className="metric">
          somme <b>{formatInt(stats.sum)}</b>
        </span>
        <span className="metric">
          n° ≤ 31 <b>{formatInt(stats.dateCount)}</b>
        </span>
        <span className="metric" title="1,00 = combinaison aussi jouée que la moyenne">
          indice de popularité <b>{formatDecimal(sharing.mainIndex, 2)}</b>
        </span>
      </div>

      <div className="metrics" style={{ marginTop: 4 }}>
        <span className="metric" title="Si cette combinaison sort, nombre attendu d’autres gagnants au rang 1">
          co-gagnants attendus <b>{formatDecimal(sharing.expectedCoWinners, 3)}</b>
        </span>
        <span className="metric" title="Part du lot de rang 1 revenant au porteur de cette grille">
          part de lot espérée <b>{formatPercent(sharing.expectedShare, 1)}</b>
        </span>
      </div>

      {actions !== null && (
        <div className="row" style={{ marginTop: 12 }}>
          {actions}
        </div>
      )}
    </div>
  );
}

export function gridText(game: GameConfig, grid: Grid): string {
  return gridToText(grid.main, grid.bonus, game.bonusLabel);
}
